'use client';

import { useState } from 'react';

import { Plus } from 'lucide-react';

import { cn } from '@kit/ui/utils';

export interface FaqItem {
  question: string;
  answer: string;
  /** Optional small link rendered under the answer (e.g. to the spec page). */
  link?: React.ReactNode;
}

/**
 * FAQ accordion. The active item is signalled by an elevated surface tint and
 * a teal question — NOT a coloured left-border stripe (impeccable BAN 1).
 * Open height animates via grid-template-rows, not height.
 */
export function FaqAccordion({ items }: { items: FaqItem[] }) {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div className="grid gap-px overflow-hidden rounded-xl border border-[var(--sel-line)] bg-[var(--sel-line)] sm:grid-cols-2">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div
            key={item.question}
            className={cn(
              'transition-colors',
              isOpen ? 'bg-[var(--sel-elev)]' : 'bg-[var(--sel-bg)]',
            )}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
              aria-controls={`faq-panel-${i}`}
              id={`faq-trigger-${i}`}
              className="flex w-full items-start justify-between gap-4 px-6 py-5 text-left focus-visible:ring-2 focus-visible:ring-[var(--sel-accent)] focus-visible:outline-none focus-visible:ring-inset"
            >
              <span
                className={cn(
                  'text-[1.0625rem] font-medium transition-colors',
                  isOpen ? 'text-[var(--sel-accent)]' : 'text-[var(--sel-ink)]',
                )}
              >
                {item.question}
              </span>
              <Plus
                aria-hidden="true"
                className={cn(
                  'mt-0.5 h-5 w-5 shrink-0 text-[var(--sel-muted)] transition-transform duration-300',
                  isOpen && 'rotate-45 text-[var(--sel-accent)]',
                )}
              />
            </button>
            <div
              id={`faq-panel-${i}`}
              role="region"
              aria-labelledby={`faq-trigger-${i}`}
              inert={!isOpen ? true : undefined}
              className={cn(
                // grid-template-rows is the one sanctioned height animation:
                // user-initiated disclosure whose purpose IS the reflow.
                'grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
              )}
            >
              <div className="overflow-hidden">
                <p
                  className={cn(
                    'px-6 text-[0.9375rem] leading-relaxed text-[var(--sel-muted)]',
                    item.link ? 'pb-3' : 'pb-6',
                  )}
                >
                  {item.answer}
                </p>
                {item.link ? (
                  <p className="px-6 pb-6 text-sm">{item.link}</p>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
